import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from '../components/marketing/MarketingLayout';
import '../styles/marketing-pricing.css';

/* ============================================================
   Clarid "Features & plans" — conversation-first pricing.
   Ported from the Claude Design handoff (Clarid Features.html).
   Two public tiers (Professional / Enterprise), ROI frame, sizing
   guide, side-by-side compare table, and FAQ accordion. No public
   numbers — pricing is sized on a call.
   ============================================================ */

interface Faq {
  q: string;
  a: string;
}
const FAQS: Faq[] = [
  {
    q: 'Why long-term terms?',
    a: 'Compliance is a continuous program, not a one-time scan. Value compounds over time — year-over-year trend data, a growing library of historical exam packages, and institutional memory that makes every subsequent exam easier. A multi-year term is how that compounding works in your favor.',
  },
  {
    q: 'Do new modules cost extra?',
    a: "No. New modules and capabilities released during your term are included at your plan level — the platform grows with you, and you're never re-quoted mid-term for what's already in your tier.",
  },
  {
    q: "What's the upgrade path?",
    a: 'Professional → Enterprise, prorated for the remainder of your term. Many institutions start on Professional and move to Enterprise as HMDA volume grows or a fair-lending or CRA exam approaches.',
  },
  {
    q: 'How is our data secured?',
    a: 'Encrypted in transit and at rest, role-based access control (RBAC), and immutable audit logs on every action. Your data is never used to train models — yours or anyone else’s.',
  },
  {
    q: 'Does it work with our AI tools?',
    a: 'Yes — Clarid exposes its compliance tools over the Model Context Protocol (MCP), so your team can query straight from Claude, ChatGPT, or Copilot, scoped to your plan and your data. MCP is included with Enterprise.',
  },
  {
    q: 'Is there a free trial?',
    a: 'We do a guided demo instead of a free trial. Compliance work is too consequential to evaluate blind — a walkthrough against your real workflows tells you far more than a sandbox ever could.',
  },
];

interface CmpRow {
  name: string;
  sub?: string;
  pro: boolean;
  ent: boolean;
}
interface CmpGroup {
  group: string;
  rows: CmpRow[];
}
const COMPARE: CmpGroup[] = [
  {
    group: 'Marketing & program',
    rows: [
      { name: 'Marketing compliance', pro: true, ent: true },
      { name: 'Compliance program management', pro: true, ent: true },
      { name: 'Regulatory monitoring', pro: true, ent: true },
      { name: 'Enforcement intelligence', pro: false, ent: true },
    ],
  },
  {
    group: 'HMDA',
    rows: [
      { name: 'LAR validation · 170+ edit checks', pro: true, ent: true },
      { name: 'Edit intelligence · geocoding · APOR', sub: 'full HMDA suite', pro: false, ent: true },
      { name: 'CFPB filing · loan-officer monitoring', pro: false, ent: true },
    ],
  },
  {
    group: 'Fair lending · CRA · regression',
    rows: [
      { name: 'Fair lending', sub: 'IFLEP · 17 risk factors', pro: false, ent: true },
      { name: 'CRA analysis', pro: false, ent: true },
      { name: 'Regression analysis', sub: 'reproduces the FRB Fair Lending Tool to the decimal', pro: false, ent: true },
      { name: 'Non-HMDA / whole-loan-book', sub: 'ALERT / ILDR', pro: false, ent: true },
    ],
  },
  {
    group: 'Exam & board',
    rows: [
      { name: 'Exam readiness scoring', pro: true, ent: true },
      { name: 'Full exam center', sub: 'one-click examiner packages', pro: false, ent: true },
    ],
  },
  {
    group: 'Platform · every plan',
    rows: [
      { name: 'Unlimited users, scans & retention', pro: true, ent: true },
      { name: 'Immutable audit trail · SHA-256 exports', pro: true, ent: true },
      { name: 'Deterministic, multi-provider AI', pro: true, ent: true },
      { name: 'API · SSO/SCIM · MCP · peer benchmarking', pro: false, ent: true },
    ],
  },
];

function Cell({ on }: { on: boolean }) {
  return on ? <span className="ck-yes">✓</span> : <span className="ck-no">—</span>;
}

export default function NewPricingPage() {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <MarketingLayout activeNav="pricing">
      {/* A · HERO */}
      <section className="price-hero">
        <div className="container">
          <div className="kicker" style={{ marginBottom: '1.6rem' }}>§&nbsp;FEATURES &amp; PLANS</div>
          <h1 className="display balance">Every capability, sized to your institution.</h1>
          <p className="lead pretty" style={{ marginTop: '1.6rem', maxWidth: '52ch' }}>
            Clarid is a long-term compliance platform, not a monthly utility. Tell us about your institution
            and we'll shape the right plan, term, and rollout.
          </p>
          <div style={{ marginTop: '2.2rem' }}>
            <Link to="/request-demo" className="btn btn-primary">Schedule a conversation <span className="arrow">→</span></Link>
          </div>
          <div className="trust-line">
            <span className="pill">Unlimited users</span>
            <span className="pill">Unlimited scans</span>
            <span className="pill">Unlimited retention</span>
            <span className="meta">on every plan</span>
          </div>
        </div>
      </section>

      {/* B · ROI FRAME */}
      <section className="section panel-bg hairline-top hairline-bottom">
        <div className="container">
          <div className="sign reveal" style={{ marginBottom: 'clamp(2.5rem,4vw,3.5rem)' }}>
            <div className="sign-row">
              <span className="kicker kicker--ink">§&nbsp;01 · THE ROI</span>
              <hr className="rule" />
            </div>
            <h2 className="h2 balance" style={{ maxWidth: '24ch' }}>One connected platform instead of five point tools.</h2>
          </div>
          <div className="roi-grid reveal">
            <div className="vendors">
              <div className="vendor"><span>Ncontracts</span><span className="vlabel">vendor</span></div>
              <div className="vendor"><span>Wolters · fair lending</span><span className="vlabel">vendor</span></div>
              <div className="vendor"><span>CRA Wiz</span><span className="vlabel">vendor</span></div>
              <div className="vendor"><span>Marketing-review tool</span><span className="vlabel">vendor</span></div>
              <div className="vendor"><span>Internal exam-prep hours</span><span className="vlabel">internal</span></div>
            </div>
            <div className="roi-arrow">→</div>
            <div className="roi-clarid">
              <div className="kicker" style={{ marginBottom: '1rem' }}>CLARID</div>
              <p className="stat-num" style={{ fontSize: '1.6rem', color: 'var(--ink)', letterSpacing: '-0.02em' }}>$45K–$200K/yr</p>
              <p className="meta" style={{ margin: '0.4rem 0 1.2rem' }}>typical community-bank spend, replaced</p>
              <p className="body">Clarid replaces the tools and the manual hours — and connects them, so a finding in one module surfaces an insight in another. One bill, one audit trail, one examiner-ready package.</p>
            </div>
          </div>
          <p className="lead reveal" style={{ marginTop: 'clamp(2rem,3.5vw,3rem)', maxWidth: '62ch', paddingTop: 'clamp(1.5rem,2.5vw,2rem)', borderTop: '1px solid var(--rule)' }}>
            Clarid is the engine community banks <span style={{ color: 'var(--ink)' }}>and their advisors</span> run on — it does the analytical grunt work so compliance teams and consultants spend their time on judgment, not spreadsheets.
          </p>
        </div>
      </section>

      {/* C · TIER CARDS */}
      <section className="section">
        <div className="container">
          <div className="sign reveal" style={{ marginBottom: 'clamp(2.5rem,4vw,3.5rem)' }}>
            <div className="sign-row">
              <span className="kicker kicker--ink">§&nbsp;02 · TWO TIERS</span>
              <hr className="rule" />
            </div>
            <h2 className="h2 balance" style={{ maxWidth: '22ch' }}>Two plans. Both conversation-first.</h2>
          </div>

          <div className="tiers reveal">
            {/* Professional */}
            <div className="tier">
              <div className="tier-name">Professional</div>
              <p className="tier-who">For community banks building a connected compliance program on a single platform.</p>
              <div className="tier-price">Let's talk</div>
              <p className="tier-price-note">sized to your institution · multi-year</p>
              <Link to="/request-demo" className="btn btn-secondary">Schedule a conversation</Link>

              <div className="cap-group">
                <span className="cap-label">Included</span>
                <ul className="cap-list">
                  <li><span className="ck">✓</span> Marketing compliance</li>
                  <li><span className="ck">✓</span> HMDA basics · LAR validation, 170+ edit checks</li>
                  <li><span className="ck">✓</span> Regulatory monitoring</li>
                  <li><span className="ck">✓</span> Exam readiness</li>
                  <li><span className="ck">✓</span> Compliance program management</li>
                </ul>
              </div>
              <div className="cap-group">
                <span className="cap-label">Platform · every plan</span>
                <ul className="cap-list">
                  <li><span className="ck">✓</span> Unlimited users, scans &amp; retention</li>
                  <li><span className="ck">✓</span> Immutable audit trail · SHA-256 exports</li>
                  <li><span className="ck">✓</span> Deterministic, multi-provider AI</li>
                </ul>
              </div>
            </div>

            {/* Enterprise */}
            <div className="tier tier--feat">
              <span className="tier-flag">Most connected</span>
              <div className="tier-name">Enterprise</div>
              <p className="tier-who">For institutions with active HMDA reporting and fair-lending, CRA &amp; exam scrutiny on the horizon.</p>
              <div className="tier-price">Let's talk</div>
              <p className="tier-price-note">sized to your institution · multi-year</p>
              <Link to="/request-demo" className="btn btn-primary">Schedule a conversation</Link>

              <p className="plus-line" style={{ marginTop: '1.3rem' }}>↳ Everything in Professional, plus:</p>
              <div className="cap-group" style={{ marginTop: '0.6rem' }}>
                <span className="cap-label">Full HMDA suite</span>
                <ul className="cap-list">
                  <li><span className="ck">✓</span> Edit intelligence · geocoding · APOR</li>
                  <li><span className="ck">✓</span> CFPB filing · loan-officer monitoring</li>
                </ul>
              </div>
              <div className="cap-group">
                <span className="cap-label">Fair lending · CRA · regression</span>
                <ul className="cap-list">
                  <li><span className="ck">✓</span> Fair lending · IFLEP, 17 risk factors</li>
                  <li><span className="ck">✓</span> CRA · regression analysis</li>
                  <li><span className="ck">✓</span> Non-HMDA / whole-loan-book (ALERT/ILDR)</li>
                </ul>
              </div>
              <div className="cap-group">
                <span className="cap-label">Intelligence · exam · platform</span>
                <ul className="cap-list">
                  <li><span className="ck">✓</span> Enforcement intelligence</li>
                  <li><span className="ck">✓</span> Full exam center · one-click packages</li>
                  <li><span className="ck">✓</span> API · SSO/SCIM · MCP · peer benchmarking</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* D · SIZING GUIDE */}
      <section className="section panel-bg hairline-top">
        <div className="container">
          <div className="sign reveal" style={{ marginBottom: 'clamp(2.5rem,4vw,3.5rem)' }}>
            <div className="sign-row">
              <span className="kicker kicker--ink">§&nbsp;03 · WHICH FITS YOUR INSTITUTION?</span>
              <hr className="rule" />
            </div>
            <h2 className="h2 balance" style={{ maxWidth: '22ch' }}>A guide, not a rule. We'll size it with you on the call.</h2>
          </div>
          <div className="sizing reveal">
            <div className="size-card">
              <span className="assets">~$500M–$1B</span>
              <h3 className="h3">Smaller community banks</h3>
              <p className="body">A focused compliance program — marketing, HMDA basics, monitoring, and exam readiness — without the full analytical suite yet.</p>
              <div className="size-rec"><span className="rec-pro">→ Lean Professional</span></div>
            </div>
            <div className="size-card">
              <span className="assets">active HMDA · fair lending</span>
              <h3 className="h3">Exams on the horizon</h3>
              <p className="body">Active HMDA reporting with fair-lending and CRA exams approaching — where the cross-module analysis earns its keep.</p>
              <div className="size-rec"><span className="rec-ent">→ Lean Enterprise</span></div>
            </div>
            <div className="size-card">
              <span className="assets">~$2B–$10B</span>
              <h3 className="h3">Larger institutions &amp; integrators</h3>
              <p className="body">$2B–$10B institutions, and anyone needing API, SSO, or partner integration to embed Clarid into their stack.</p>
              <div className="size-rec"><span className="rec-ent">→ Enterprise</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* E · COMPARE TABLE */}
      <section className="section">
        <div className="container">
          <div className="sign reveal" style={{ marginBottom: 'clamp(2rem,3.5vw,3rem)' }}>
            <div className="sign-row">
              <span className="kicker kicker--ink">§&nbsp;04 · COMPARE PLANS</span>
              <hr className="rule" />
            </div>
            <h2 className="h2 balance" style={{ maxWidth: '20ch' }}>Every module, side by side.</h2>
          </div>
          <div className="cmp-wrap reveal">
            <table className="cmp">
              <thead>
                <tr>
                  <th>Capability</th>
                  <th><span className="cmp-head-tier">Professional</span></th>
                  <th><span className="cmp-head-tier">Enterprise</span></th>
                </tr>
              </thead>
              <tbody>
                {COMPARE.map((g) => (
                  <Fragment key={g.group}>
                    <tr className="grp"><td colSpan={3}>{g.group}</td></tr>
                    {g.rows.map((r) => (
                      <tr key={g.group + r.name}>
                        <td className="feat-name">
                          {r.name}
                          {r.sub && <span className="feat-sub">{r.sub}</span>}
                        </td>
                        <td><Cell on={r.pro} /></td>
                        <td><Cell on={r.ent} /></td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <p className="meta reveal" style={{ marginTop: '1.2rem', color: 'var(--ink-4)' }}>— = not included at this tier · teal ✓ = included. Modules are grouped so the cross-module breadth is visible.</p>
        </div>
      </section>

      {/* F · FAQ / RATIONALE */}
      <section className="section panel-bg hairline-top hairline-bottom">
        <div className="container">
          <div className="sign reveal" style={{ marginBottom: 'clamp(2rem,3.5vw,3rem)' }}>
            <div className="sign-row">
              <span className="kicker kicker--ink">§&nbsp;05 · A PLATFORM, NOT A SUBSCRIPTION UTILITY</span>
              <hr className="rule" />
            </div>
            <h2 className="h2 balance" style={{ maxWidth: '24ch' }}>Why Clarid is sold as a relationship, not a monthly charge.</h2>
          </div>
          <div className="faq reveal">
            {FAQS.map((f, i) => (
              <div className={`faq-item${openFaq === i ? ' open' : ''}`} key={f.q}>
                <button className="faq-q" onClick={() => setOpenFaq(openFaq === i ? -1 : i)}>
                  {f.q} <span className="sign-toggle">+</span>
                </button>
                <div className="faq-a">
                  <div className="faq-a-inner">{f.a}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* G · FINAL CTA */}
      <section className="section final">
        <div className="container">
          <div className="reveal" style={{ maxWidth: '40ch' }}>
            <div className="kicker" style={{ color: 'var(--teal-bright)', marginBottom: '1.6rem' }}>§&nbsp;06 · LET'S TALK</div>
            <h2 className="h2 balance">Let's size Clarid to your institution.</h2>
            <p className="lead" style={{ color: 'rgba(243,241,234,0.7)', marginTop: '1.5rem' }}>
              A 30-minute conversation about your asset range, modules, term, and rollout — then a plan shaped to fit.
            </p>
            <div className="final-cta">
              <Link to="/request-demo" className="btn btn-on-ink">Schedule a conversation <span className="arrow">→</span></Link>
              <Link to="/request-demo" className="btn btn-on-ink-ghost">Request a demo</Link>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
